/**
 * v3.lexisnexis — KBA + InstantID-search scaffold (Bill 2026-05-10 v0.14.17).
 *
 * Server: PCBJxChangeApi/Functions/LexisNexisFunction.cs.
 *
 * Five routes:
 *   GET  /v3/lexisnexis/enabled                         (Officer+)
 *   POST /v3/lexisnexis/kba/generate                    (Officer+)
 *   POST /v3/lexisnexis/kba/verify                      (Officer+)
 *   POST /v3/lexisnexis/instantid/search                (Officer+)
 *   GET  /v3/admin/lexisnexis/call-log                  (Admin+)
 *
 * Defaults to DISABLED at v3.0 launch (SystemSettings `lexisnexis.enabled`
 * defaults to false). When disabled the four scaffold endpoints return 503
 * with errorCode `LEXISNEXIS_DISABLED` so callers can route to DIY KBA.
 *
 * Exposed at `client.retail.lexisNexis.*` (KBA/InstantID surface) and
 * `client.retail.admin.lexisNexisConfig.*` (Admin call-log viewer).
 */

import type {
  RequestOptions,
  LexisNexisEnabledOutcome,
  LexisNexisKbaInput,
  LexisNexisKbaOutcome,
  LexisNexisKbaVerifyInput,
  LexisNexisKbaVerifyOutcome,
  LexisNexisInstantIdInput,
  LexisNexisInstantIdOutcome,
  LexisNexisCallLogFilter,
  LexisNexisCallLogRow,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// ──────────────────────────────────────────────────────────────────────────
// Officer-scoped KBA / InstantID-search surface — client.retail.lexisNexis.*
// ──────────────────────────────────────────────────────────────────────────

export class LexisNexisApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /** GET /v3/lexisnexis/enabled — returns {enabled, reason, environment}. */
  public isEnabled(
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: LexisNexisEnabledOutcome }> {
    return this.http.get<{ success: boolean; data: LexisNexisEnabledOutcome }>(
      '/v3/lexisnexis/enabled',
      { ...opts },
    );
  }

  /** POST /v3/lexisnexis/kba/generate — when disabled returns 503 LEXISNEXIS_DISABLED. */
  public generateKba(
    body: LexisNexisKbaInput,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: LexisNexisKbaOutcome }> {
    return this.http.post<{ success: boolean; data: LexisNexisKbaOutcome }>(
      '/v3/lexisnexis/kba/generate',
      { ...opts, body },
    );
  }

  /** POST /v3/lexisnexis/kba/verify — answers[] never logged. */
  public verifyKba(
    body: LexisNexisKbaVerifyInput,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: LexisNexisKbaVerifyOutcome }> {
    return this.http.post<{ success: boolean; data: LexisNexisKbaVerifyOutcome }>(
      '/v3/lexisnexis/kba/verify',
      { ...opts, body },
    );
  }

  /** POST /v3/lexisnexis/instantid/search — non-interactive alternate path. */
  public instantIdSearch(
    body: LexisNexisInstantIdInput,
    opts?: RequestOptions,
  ): Promise<{ success: boolean; data: LexisNexisInstantIdOutcome }> {
    return this.http.post<{ success: boolean; data: LexisNexisInstantIdOutcome }>(
      '/v3/lexisnexis/instantid/search',
      { ...opts, body },
    );
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Admin call-log viewer — client.retail.admin.lexisNexisConfig.*
// ──────────────────────────────────────────────────────────────────────────

export interface LexisNexisCallLogListResponse {
  success: boolean;
  count: number;
  rows: ReadonlyArray<LexisNexisCallLogRow>;
}

export class LexisNexisConfigAdminApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * GET /v3/admin/lexisnexis/call-log — Admin+. The config-panel itself
   * uses the typed SystemSettings admin surface (writes flow through
   * `client.retail.admin.systemSettings.upsert(...)` per the 8 seeded keys).
   * No separate config-write endpoint — SystemSettings is the source of
   * truth.
   */
  public listCalls(
    filter: LexisNexisCallLogFilter = {},
    opts?: RequestOptions,
  ): Promise<LexisNexisCallLogListResponse> {
    const qs = new URLSearchParams();
    if (filter.fromUtc) qs.set('fromUtc', filter.fromUtc);
    if (filter.toUtc) qs.set('toUtc', filter.toUtc);
    if (filter.cartId) qs.set('cartId', filter.cartId);
    if (filter.product) qs.set('product', filter.product);
    if (filter.cifId) qs.set('cifId', filter.cifId);
    if (filter.top) qs.set('top', String(filter.top));
    const query = qs.toString();
    const path = query
      ? `/v3/admin/lexisnexis/call-log?${query}`
      : '/v3/admin/lexisnexis/call-log';
    return this.http.get<LexisNexisCallLogListResponse>(path, { ...opts });
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Module augmentation — extend AdminApi with `.lexisNexisConfig`.
// ──────────────────────────────────────────────────────────────────────────

declare module './admin.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface AdminApi {
    /** LexisNexis scaffold call-log viewer (Bill 2026-05-10 v0.14.17). */
    readonly lexisNexisConfig: LexisNexisConfigAdminApi;
  }
}
